// Ask-user card component.
// Copied verbatim from frontend/src/components/agent/AskUserCard.tsx.
// Fully generic — no Platinum-specific dependencies.
// See ../../docs/90-provenance-map.md.
//
// Two modes, chosen by question.multiSelect (the ask_user tool's multi_select):
//   - single choice (the default, and every event recorded before multi-select
//     existed): clicking an option answers at once with its value.
//   - multi-select: options are toggles (aria-pressed) and ONE Submit sends a
//     single message built by formatMultiSelectAnswer (../askUserAnswer.ts).
//     With allow_freetext the text box feeds that same Submit. An option marked
//     `advance` stays a one-click action and is never a toggle: it moves the
//     phase on rather than answering, so it cannot be one item of a list.
// Once answered, both modes render from question.selectedValue (the sent
// message), so a live answer and a replayed one look the same.
//
// With onPick (guided conversations, Adam 29 Oct) the card sends nothing: a
// click, or the multi-select Submit, hands the text to the host, which puts it
// in the composer for the person to add to and send. The composer is then the
// free-text box, so the card draws none.

import React, { useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import type { AskUserOption, AskUserQuestionInfo } from '../types.js'
import { formatMultiSelectAnswer, joinNaturally, parseMultiSelectAnswer } from '../askUserAnswer.js'
import { CARD_SX, HAIRLINE_HOVER_COLOR, INNER_RADIUS, PILL_RADIUS } from './surface.js'

interface AskUserCardProps {
  question: AskUserQuestionInfo
  onAnswer: (value: string) => void
  /** Set, a pick goes to the host instead of being sent. */
  onPick?: (value: string) => void
  onAdvance?: () => void
  disabled: boolean
}

export default function AskUserCard({ question, onAnswer, onPick, onAdvance, disabled }: AskUserCardProps) {
  const [freetextValue, setFreetextValue] = useState('')
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set())
  const isAnswered = question.answered
  const multi = question.multiSelect === true

  const answer = onPick ?? onAnswer
  // Multi-select: what shows as chosen comes from the sent message once answered
  // (live or replayed), and from local toggles until then.
  const answeredMulti = multi && isAnswered ? parseMultiSelectAnswer(question.selectedValue ?? '', question.options) : null
  // A single choice answered through the composer may go on in the person's
  // own words after the option.
  const answeredSingle = !multi && isAnswered ? parseMultiSelectAnswer(question.selectedValue ?? '', question.options).values : []
  const isChosen = (value: string) => (answeredMulti ? answeredMulti.values.includes(value) : chosen.has(value))
  const toggle = (value: string) => setChosen(prev => {
    const next = new Set(prev)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    return next
  })
  // Option order, not click order, so the message reads the way the card does.
  const chosenValues = question.options.map(o => o.value).filter(v => chosen.has(v))
  const canSubmitMulti = chosenValues.length > 0 || (question.allowFreetext && freetextValue.trim() !== '')

  const renderOption = (opt: AskUserOption) => {
    const isAdvanceOption = !!(opt.advance && onAdvance)
    const toggleable = multi && !isAdvanceOption
    const isSelected = toggleable ? isChosen(opt.value) : isAnswered && (question.selectedValue === opt.value || answeredSingle.includes(opt.value))
    return (
      <Button
        key={opt.value}
        variant={isSelected ? 'contained' : 'text'}
        color={isSelected ? 'primary' : 'inherit'}
        size="small"
        disabled={disabled || isAnswered}
        aria-pressed={toggleable ? isSelected : undefined}
        onClick={() => {
          if (isAdvanceOption) {
            onAdvance!()
          } else if (toggleable) {
            toggle(opt.value)
          } else {
            answer(opt.value)
          }
        }}
        endIcon={isAdvanceOption ? <ArrowForwardIcon sx={{ fontSize: 16 }} /> : undefined}
        sx={{
          textTransform: 'none',
          // One line is a pill (the header's shape); a described option
          // is a small box one level inside the card, because a pill cannot
          // hold two lines well.
          borderRadius: opt.description ? INNER_RADIUS : PILL_RADIUS,
          flexDirection: 'column',
          alignItems: 'flex-start',
          px: 2,
          py: opt.description ? 1 : 0.75,
          fontSize: '0.75rem',
          color: isSelected ? 'white' : 'text.secondary',
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          // A pressed toggle keeps its fill under the pointer, or hovering it
          // would read as un-choosing it.
          '&:hover': toggleable && isSelected
            ? { backgroundColor: 'primary.dark', borderColor: 'primary.dark', color: 'white' }
            : {
                backgroundColor: 'action.hover',
                borderColor: HAIRLINE_HOVER_COLOR,
                color: 'text.primary',
              },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Chosen is shown by a mark as well as by colour. */}
          {toggleable && isSelected && <CheckIcon aria-hidden sx={{ fontSize: 14 }} />}
          <span>{opt.label}</span>
        </Box>
        {opt.description && (
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              opacity: 0.8,
              fontWeight: 400,
              lineHeight: 1.3,
              textAlign: 'left',
            }}
          >
            {opt.description}
          </Typography>
        )}
      </Button>
    )
  }

  const freetextField = (
    <TextField
      size="small"
      placeholder="Or type your answer..."
      value={freetextValue}
      onChange={e => setFreetextValue(e.target.value)}
      disabled={disabled}
      sx={{
        flex: 1,
        '& .MuiInputBase-input': { fontSize: 13 },
        '& .MuiOutlinedInput-root': { borderRadius: PILL_RADIUS, pl: 0.5 },
      }}
    />
  )

  let answeredText = question.selectedValue
  if (answeredMulti) {
    const labels = question.options.filter(o => answeredMulti.values.includes(o.value)).map(o => o.label)
    const parts = [joinNaturally(labels), answeredMulti.rest].filter(Boolean)
    if (parts.length > 0) answeredText = parts.join(' — ')
  }

  return (
    <Box
      data-testid="ask-user-card"
      sx={{
        ...CARD_SX,
        p: 2,
        mt: 1,
        maxWidth: 500,
      }}
    >
      <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem', mb: question.context ? 0.5 : 1 }}>
        {question.question}
      </Typography>

      {question.context && (
        <Typography sx={{ color: '#4b5563', fontSize: 13, mb: 1 }}>
          {question.context}
        </Typography>
      )}

      <Box
        role={multi ? 'group' : undefined}
        aria-label={multi ? question.question : undefined}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
      >
        {question.options.map(renderOption)}
      </Box>

      {!multi && question.allowFreetext && !isAnswered && !onPick && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          {freetextField}
          <Button
            variant="text"
            size="small"
            disabled={disabled || !freetextValue.trim()}
            onClick={() => onAnswer(freetextValue.trim())}
            sx={{ textTransform: 'none', borderRadius: PILL_RADIUS, px: 1.75, color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' } }}
          >
            Submit
          </Button>
        </Box>
      )}

      {multi && !isAnswered && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', justifyContent: 'flex-end' }}>
          {question.allowFreetext && !onPick && freetextField}
          <Button
            variant="contained"
            color="info"
            size="small"
            disableElevation
            disabled={disabled || !canSubmitMulti}
            onClick={() => answer(formatMultiSelectAnswer(chosenValues, question.allowFreetext && !onPick ? freetextValue : ''))}
            sx={{ textTransform: 'none', borderRadius: PILL_RADIUS, px: 2 }}
          >
            Submit
          </Button>
        </Box>
      )}

      {isAnswered && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1, whiteSpace: 'pre-wrap' }}>
          Answered: {answeredText}
        </Typography>
      )}
    </Box>
  )
}
